import { CheckCircle2, Clock, Copy, RefreshCw, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DELIVERY_COPY,
  type ProfileDeliveryState
} from '@/services/publicProfileDeliveryService';

interface ProfileDeliveryPanelProps {
  state: ProfileDeliveryState | null;
  loading: boolean;
  requesting: boolean;
  canRequestBuild: boolean;
  onRequestBuild: () => Promise<{ ok: boolean }>;
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  live: 'default',
  stale: 'secondary',
  building: 'secondary',
  pending_build: 'outline',
  not_published: 'outline',
  unknown: 'outline',
  build_failed: 'destructive'
};

/**
 * Honest delivery reporting for an officer's public page.
 *
 * A requested build is not delivery: this panel reports Live only when the
 * deployed site manifest publishes the same content revision the database
 * returns right now. Until then the shareable link stays the directory
 * anchor, which always resolves.
 */
export function ProfileDeliveryPanel({
  state,
  loading,
  requesting,
  canRequestBuild,
  onRequestBuild
}: ProfileDeliveryPanelProps) {
  const [copied, setCopied] = useState(false);
  const shareUrlId = useId();

  if (!state) {
    return null;
  }

  const copy = DELIVERY_COPY[state.status];
  const generated = state.manifestGeneratedAt
    ? new Date(state.manifestGeneratedAt).toLocaleString()
    : null;

  const copyShareUrl = async () => {
    if (!state.shareUrl) {
return;
}
    await navigator.clipboard.writeText(state.shareUrl);
    setCopied(true);
    toast.success('Public profile link copied');
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="border-primary/20 bg-primary/[0.025]" data-testid="profile-delivery-panel">
      <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            {state.status === 'live' ? (
              <CheckCircle2 className="h-5 w-5 text-success" aria-hidden="true" />
            ) : state.status === 'build_failed' ? (
              <TriangleAlert className="h-5 w-5 text-destructive" aria-hidden="true" />
            ) : (
              <Clock className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            )}
            <CardTitle className="text-lg">Public page delivery</CardTitle>
            <Badge variant={STATUS_VARIANT[state.status] ?? 'outline'}>
              {loading ? 'Checking…' : copy.label}
            </Badge>
          </div>
          <CardDescription className="mt-2">{copy.detail}</CardDescription>
        </div>
        {canRequestBuild && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={requesting}
            onClick={() => void onRequestBuild()}
          >
            <RefreshCw className={`h-4 w-4 ${requesting ? 'animate-spin' : ''}`} aria-hidden="true" />
            {requesting ? 'Requesting…' : 'Request rebuild'}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {state.shareUrl && (
          <div>
            <Label htmlFor={shareUrlId} className="mb-1 block text-sm font-medium leading-5">
              {state.permanentUrlDelivered ? 'Public profile link' : 'Shareable link (fallback)'}
            </Label>
            <div className="flex items-center gap-2 rounded-lg bg-muted p-2">
              <Input
                id={shareUrlId}
                value={state.shareUrl}
                readOnly
                className="flex-1 border-0 bg-transparent"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Copy public profile link"
                onClick={() => void copyShareUrl()}
              >
                {copied ? (
                  <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
                ) : (
                  <Copy className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </div>
            {!state.permanentUrlDelivered && (
              <p className="mt-1 text-xs text-muted-foreground">
                The permanent per-officer link is shared only after the published site confirms
                it. Until then this directory link always resolves.
              </p>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          {generated ? `Published site last built ${generated}.` : 'Published site build time unknown.'}
          {state.indexable
            ? ' This page is approved and eligible for search indexing.'
            : ' This page is published with noindex until approval and content checks pass.'}
        </p>
      </CardContent>
    </Card>
  );
}
